/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   salaryStructure.config.ts

   Single source of truth for the structure-type driven Salary Structure
   screens. `structureType` is a REAL persisted enum column on the backend
   (SalaryStructureType): PERMANENT_EMPLOYEE, CONTRACT_EMPLOYEE, TRAINEE,
   INTERN, CONSULTANT.

   The entity groups fields into three templates, so the five types map onto
   three field sets:
     • EMPLOYEE  → PERMANENT_EMPLOYEE
     • CONTRACT  → CONTRACT_EMPLOYEE, CONSULTANT   (GST / non-GST)
     • TRAINEE   → TRAINEE, INTERN                  (part-time stipend)

   Sections render from SECTIONS_BY_TEMPLATE — never from inline `&&` chains.
   ──────────────────────────────────────────────────────────────────────── */

// The persisted enum, matching the backend SalaryStructureType exactly.
export type StructureType =
  | "PERMANENT_EMPLOYEE"
  | "CONTRACT_EMPLOYEE"
  | "TRAINEE"
  | "INTERN"
  | "CONSULTANT";

// The three field-set templates the five types share.
export type TemplateId = "EMPLOYEE" | "CONTRACT" | "TRAINEE";

export const TEMPLATE_BY_TYPE: Record<StructureType, TemplateId> = {
  PERMANENT_EMPLOYEE: "EMPLOYEE",
  CONTRACT_EMPLOYEE: "CONTRACT",
  CONSULTANT: "CONTRACT",
  TRAINEE: "TRAINEE",
  INTERN: "TRAINEE",
};

export const templateForType = (type: StructureType): TemplateId =>
  TEMPLATE_BY_TYPE[type] ?? "EMPLOYEE";

export type SectionId =
  | "basic"
  | "empSalary"
  | "empAllowances"
  | "empDeductions"
  | "contractDetails"
  | "traineeComp"
  | "traineeBenefits";

// Which sections each template renders, in order (basic info is shared by all).
export const SECTIONS_BY_TEMPLATE: Record<TemplateId, SectionId[]> = {
  EMPLOYEE: ["basic", "empSalary", "empAllowances", "empDeductions"],
  CONTRACT: ["basic", "contractDetails"],
  TRAINEE: ["basic", "traineeComp", "traineeBenefits"],
};

export const sectionsForType = (type: StructureType): SectionId[] =>
  SECTIONS_BY_TEMPLATE[templateForType(type)];

// Step-1 selector cards. Because Contractor/Consultant and Trainee/Intern share
// an identical field set, they are combined into a single card each — one card
// per TEMPLATE, not per enum value. The `type` is the canonical enum persisted
// when that card is chosen (contract card → CONTRACT_EMPLOYEE, stipend card →
// TRAINEE). Legacy CONSULTANT / INTERN rows still render via their template.
export interface TypeCard {
  type: StructureType;
  title: string;
  description: string;
}
export const TYPE_CARDS: TypeCard[] = [
  {
    type: "PERMANENT_EMPLOYEE",
    title: "Permanent Employee",
    description: "Fixed salary, allowances, benefits and employee deductions.",
  },
  {
    type: "CONTRACT_EMPLOYEE",
    title: "Contractor & Consultant",
    description: "Contract / consultant engagement billed with GST / TDS.",
  },
  {
    type: "TRAINEE",
    title: "Trainee & Intern",
    description: "Stipend-based compensation for a training or internship period.",
  },
];

// Coloured badge tokens per type — list screen, view screen, form pill.
export const TYPE_BADGE: Record<StructureType, { label: string; className: string }> = {
  PERMANENT_EMPLOYEE: {
    label: "Permanent Employee",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-300",
  },
  CONTRACT_EMPLOYEE: {
    label: "Contract Employee",
    className:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border border-purple-300",
  },
  CONSULTANT: {
    label: "Consultant",
    className:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-300",
  },
  TRAINEE: {
    label: "Trainee",
    className: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border border-teal-300",
  },
  INTERN: {
    label: "Intern",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300",
  },
};

/* ── Per-template default field values ──────────────────────────────────── */
// Shared basic-info defaults for every type (Structure Name + Description).
const BASIC_DEFAULTS = {
  structureName: "",
  description: "",
};

// EMPLOYEE — mirrors the legacy add-form defaults so existing behaviour is
// preserved; statutory model keeps the PF/ESI/PT/Noida-PT toggles as-is.
const EMPLOYEE_DEFAULTS = {
  basicSalaryPercentage: 50,
  hraPercentage: 20,
  halfYearBonusPercentage: 0,
  performanceBonusPercentage: 0,
  annualBonusPercentage: undefined as number | undefined,
  annualBonusIsPercentage: true,
  conveyanceAllowance: 12000,
  conveyanceIsPercentage: false,
  medicalAllowance: 15000,
  medicalAllowanceIsPercentage: false,
  leaDays: 12,
  leaMonthly: undefined as number | undefined,
  leaIsPercentage: false,
  medicalInsuranceApplicable: false,
  medicalInsuranceMonthly: undefined as number | undefined,
  medicalInsuranceIsPercentage: false,
  pfApplicable: false,
  pfWageCeilingApplicable: false,
  providentFundEmployeePercentage: 12,
  providentFundEmployerPercentage: 12,
  esiApplicable: false,
  esiEmployerPercentage: 3.25,
  esiEmployeePercentage: 0.75,
  profTaxApplicable: false,
  taxOnProfession: 0,
  noidaPtApplicable: false,
};

// CONTRACT — one Contract Type choice (GST / Non-GST) drives the rest.
const CONTRACT_DEFAULTS = {
  contractType: "GST" as "GST" | "NON_GST",
  contractValue: undefined as number | undefined,
  monthlyPayout: undefined as number | undefined,
  tdsRate: 10,
  gstPercentage: 18,
};

// TRAINEE / INTERN — stipend + period (months), opt-in benefits.
const TRAINEE_DEFAULTS = {
  monthlyStipend: undefined as number | undefined,
  periodMonths: undefined as number | undefined,
  pfApplicable: false,
  esiApplicable: false,
  insuranceApplicable: false,
};

export const defaultsFor = (type: StructureType): Record<string, any> => {
  const base = { structureType: type, ...BASIC_DEFAULTS };
  const template = templateForType(type);
  if (template === "EMPLOYEE") return { ...base, ...EMPLOYEE_DEFAULTS };
  if (template === "CONTRACT") return { ...base, ...CONTRACT_DEFAULTS };
  return { ...base, ...TRAINEE_DEFAULTS };
};
